/* eslint-disable jsx-a11y/anchor-is-valid */
import React from "react";
import { parseProductBadges } from "../Utils/product-badges";

const BADGE_DISPLAY_DURATION = 2000;
const BADGE_TRANSITION_DURATION = 350;

const ProductBadge = ({ badge, className = "" }) => (
  <label
    className={`product-badge ${badge.colorClass} ${className}`.trim()}
  >
    {badge.label}
  </label>
);

const useBadgeRotation = (enabled, badgeKey, badgeCount) => {
  const containerRef = React.useRef(null);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [isSliding, setIsSliding] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(true);
  const [isHovered, setIsHovered] = React.useState(false);
  const [reducedMotion, setReducedMotion] = React.useState(() =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function"
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false,
  );

  React.useEffect(() => {
    setActiveIndex(0);
    setIsSliding(false);
  }, [badgeKey]);

  React.useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handleChange = (event) => setReducedMotion(event.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  React.useEffect(() => {
    if (reducedMotion) {
      setActiveIndex(0);
      setIsSliding(false);
    }
  }, [reducedMotion]);

  React.useEffect(() => {
    if (
      !enabled ||
      !containerRef.current ||
      !("IntersectionObserver" in window)
    ) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.01 },
    );
    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [enabled]);

  React.useEffect(() => {
    if (!enabled || !containerRef.current) {
      return undefined;
    }

    const productCard = containerRef.current.closest(
      ".product-box, .cart-box",
    );
    if (!productCard) {
      return undefined;
    }

    const pause = () => setIsHovered(true);
    const resume = () => setIsHovered(false);
    productCard.addEventListener("mouseenter", pause);
    productCard.addEventListener("mouseleave", resume);

    return () => {
      productCard.removeEventListener("mouseenter", pause);
      productCard.removeEventListener("mouseleave", resume);
    };
  }, [enabled]);

  React.useEffect(() => {
    if (
      !enabled ||
      reducedMotion ||
      !isVisible ||
      isHovered ||
      isSliding
    ) {
      return undefined;
    }

    const timer = window.setTimeout(
      () => setIsSliding(true),
      BADGE_DISPLAY_DURATION,
    );
    return () => window.clearTimeout(timer);
  }, [activeIndex, enabled, isHovered, isSliding, isVisible, reducedMotion]);

  React.useEffect(() => {
    if (!isSliding) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      // Batch index advance + track reset so the visible badge
      // stays continuous (old next becomes new current at 0%).
      setActiveIndex((current) => (current + 1) % badgeCount);
      setIsSliding(false);
    }, BADGE_TRANSITION_DURATION);

    return () => window.clearTimeout(timer);
  }, [badgeCount, isSliding]);

  return {
    activeIndex,
    containerRef,
    isSliding: isSliding && !reducedMotion,
  };
};

/**
 * Renders product badges using existing label markup + badge-color-* utilities.
 * Pass `fallback` to preserve legacy labels (e.g. "new") when no BADGE_ tags exist.
 * Pass `className` to wrap badges in a positioned container (e.g. PDP slider overlay).
 * Pass `rotate` only for product cards to show one rotating badge at a time.
 */
function ProductBadges({ tags, fallback = null, className, rotate = false }) {
  const badges = parseProductBadges(tags);
  const shouldRotate = rotate && badges.length > 1;
  const badgeKey = badges.map((badge) => badge.rawTag).join("|");
  const { activeIndex, containerRef, isSliding } = useBadgeRotation(
    shouldRotate,
    badgeKey,
    badges.length,
  );

  if (!badges.length) {
    return fallback;
  }

  if (shouldRotate) {
    const nextIndex = (activeIndex + 1) % badges.length;
    const currentBadge = badges[activeIndex];
    const nextBadge = badges[nextIndex];

    return (
      <div
        ref={containerRef}
        className="product-badge-carousel"
        aria-live="off"
      >
        {/* Invisible sizer keeps viewport height to one badge so overflow clips cleanly. */}
        <div className="product-badge-carousel__sizer" aria-hidden="true">
          <ProductBadge
            badge={currentBadge}
            className="product-badge-carousel__item"
          />
        </div>
        <div
          className={
            isSliding
              ? "product-badge-carousel__track product-badge-carousel__track--sliding"
              : "product-badge-carousel__track"
          }
        >
          <div className="product-badge-carousel__slide">
            <ProductBadge
              key={`current-${currentBadge.rawTag}`}
              badge={currentBadge}
              className="product-badge-carousel__item"
            />
          </div>
          <div className="product-badge-carousel__slide">
            <ProductBadge
              key={`next-${nextBadge.rawTag}`}
              badge={nextBadge}
              className="product-badge-carousel__item"
            />
          </div>
        </div>
      </div>
    );
  }

  const badgeElements = badges.map((badge) => (
    <ProductBadge key={badge.rawTag} badge={badge} />
  ));

  if (className) {
    return <div className={className}>{badgeElements}</div>;
  }

  return badgeElements;
}

export default ProductBadges;
